import { api } from './axios';
import type {
  AgeGroup,
  BoardLayout,
  BoardSortMode,
  Category,
  Competition,
  CompetitionStatus,
  CompetitionTemplate,
  DisciplineType,
  PenaltyTrackingMode,
  PublicResultsLayout,
  PublicThemeMode,
  Team,
  TimeFormat,
  User,
} from '../types/domain';

export interface CreateCompetitionDto {
  name: string;
  competitionDate: string;
  location?: string;
  unitId?: number | null;
  npPenaltyRank?: number | null;
  tieBreakerEnabled?: boolean;
  penaltyTrackingMode?: PenaltyTrackingMode;
  stafetaDvojicAttempts?: number;
  stafetaDvojicParallelTracks?: number;
  templateId?: number | null;
}

export interface UpdateCompetitionDto {
  name: string;
  competitionDate: string;
  location?: string;
  status: CompetitionStatus;
  npPenaltyRank?: number | null;
  tieBreakerEnabled?: boolean;
  penaltyTrackingMode?: PenaltyTrackingMode;
  stafetaDvojicAttempts?: number;
  stafetaDvojicParallelTracks?: number;
  overallRankingVisible?: boolean;

  // Konfigurace verejneho zobrazeni
  publicResultsLayout?: PublicResultsLayout;
  publicShowStartNumber?: boolean;
  publicShowPenaltySeconds?: boolean;
  publicShowPoints?: boolean;
  publicShowAttemptDetails?: boolean;
  publicShowLaneDetails?: boolean;
  publicShowNote?: boolean;
  publicRotationSeconds?: number;
  publicRotateOverall?: boolean;
  publicAutoRefreshSeconds?: number;
  publicThemeMode?: PublicThemeMode;
  publicShowLogo?: boolean;
  publicShowOrganizerLogo?: boolean;
  publicOrganizerLogoUrl?: string | null;
  /** Skalovani velikosti organizer loga v procentech (50-200). */
  publicOrganizerLogoScale?: number;

  boardLayout?: BoardLayout;
  boardThemeMode?: PublicThemeMode;
  /** Skalovani pisma boardu v procentech (50-200, default 100). */
  boardFontScale?: number;
  /** Zobrazit vsechna druzstva (i bez vysledku) na boardu? */
  boardShowAllTeams?: boolean;
  /** Razeni na boardu: RANK / START_NUMBER. */
  boardSortMode?: BoardSortMode;
  /** Zobrazit casy jednotlivych drah (LP/PP, Draha 1/2) pod urednim casem na boardu? */
  boardShowLaneDetails?: boolean;
  /** Zobrazit sporic na boardu dokud je soutez DRAFT? */
  boardScreensaverEnabled?: boolean;
  /** Volitelny text na sporici. "" = smazat, null = nemenit. */
  boardCustomText?: string | null;
  /** Volitelny disclaimer ve footeru verejne stranky. "" = smazat, null = nemenit. */
  publicDisclaimerText?: string | null;

  falseStartThreshold?: number;

  /** Verejna viditelnost souteze. */
  publicVisible?: boolean;
  /** Naplanovany okamzik zverejneni (ISO datetime). */
  publishAt?: string | null;
  /** Explicitni priznak odstraneni plan. publishAt v JSON se jinak nerozlisi od "nemenit". */
  clearPublishAt?: boolean;
  /** Zobrazit soutez na rozcestniku? Backend ji ignoruje pokud unit nema capability. */
  landingVisible?: boolean;

  /** Hlavni rozhodci — pro PDF (podpis). "" = smazat, undefined = nemenit. */
  headJudgeName?: string;
  /** Velitel soutezi — pro PDF (podpis). "" = smazat, undefined = nemenit. */
  eventLeaderName?: string;
  /** Komisar soutezi (jen krajska/republikova kola). "" = smazat, undefined = nemenit. */
  commissarName?: string;
}

export interface CreateCategoryDto {
  name: string;
  ageGroup: AgeGroup;
  sortOrder?: number;
}

export interface CreateDisciplineDto {
  name: string;
  type: DisciplineType;
  attemptsCount?: number;
  sortOrder?: number;
  plamenOrder?: number;
  timeFormat?: TimeFormat;
  useSecondTimer?: boolean;
  isIndividual?: boolean;
  teamMaxRunners?: number | null;
  teamScoringCount?: number | null;
  teamMinRequired?: number | null;
}

export interface CreateTeamDto {
  name: string;
  startNumber?: number;
  sdhUnitName?: string;
  note?: string;
}

export const adminApi = {
  // Competitions
  listCompetitions: (unitId?: number | null) =>
    api
      .get<Competition[]>('/admin/competitions', {
        params: unitId ? { unitId } : undefined,
      })
      .then((r) => r.data),
  competitionDetail: (id: number) =>
    api.get<Competition>(`/admin/competitions/${id}`).then((r) => r.data),
  competitionCategories: (id: number) =>
    api.get<Category[]>(`/admin/competitions/${id}/categories`).then((r) => r.data),
  createCompetition: (dto: CreateCompetitionDto) =>
    api.post<Competition>('/admin/competitions', dto).then((r) => r.data),
  updateCompetition: (id: number, dto: UpdateCompetitionDto) =>
    api.put<Competition>(`/admin/competitions/${id}`, dto).then((r) => r.data),
  deleteCompetition: (id: number) => api.delete(`/admin/competitions/${id}`),
  uploadOrganizerLogo: (id: number, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api
      .post<Competition>(`/admin/competitions/${id}/organizer-logo`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },
  deleteOrganizerLogo: (id: number) =>
    api.delete<Competition>(`/admin/competitions/${id}/organizer-logo`).then((r) => r.data),

  // Categories
  createCategory: (competitionId: number, dto: CreateCategoryDto) =>
    api.post<Category>(`/admin/competitions/${competitionId}/categories`, dto).then((r) => r.data),
  updateCategory: (id: number, dto: CreateCategoryDto) =>
    api.put<Category>(`/admin/categories/${id}`, dto).then((r) => r.data),
  deleteCategory: (id: number) => api.delete(`/admin/categories/${id}`),

  // Disciplines
  createDiscipline: (categoryId: number, dto: CreateDisciplineDto) =>
    api.post(`/admin/categories/${categoryId}/disciplines`, dto).then((r) => r.data),
  updateDiscipline: (id: number, dto: CreateDisciplineDto) =>
    api.put(`/admin/disciplines/${id}`, dto).then((r) => r.data),
  updateDisciplineTimeFormat: (id: number, timeFormat: 'SS_CC' | 'MM_SS_CC') =>
    api.patch(`/admin/disciplines/${id}/time-format`, { timeFormat }).then((r) => r.data),
  deleteDiscipline: (id: number) => api.delete(`/admin/disciplines/${id}`),

  // Teams
  listTeams: (categoryId: number) =>
    api.get<Team[]>(`/admin/categories/${categoryId}/teams`).then((r) => r.data),
  createTeam: (categoryId: number, dto: CreateTeamDto) =>
    api.post<Team>(`/admin/categories/${categoryId}/teams`, dto).then((r) => r.data),
  updateTeam: (id: number, dto: CreateTeamDto) =>
    api.put<Team>(`/admin/teams/${id}`, dto).then((r) => r.data),
  reorderTeam: (id: number, toStartNumber: number) =>
    api
      .put<Team>(`/admin/teams/${id}/reorder`, null, { params: { toStartNumber } })
      .then((r) => r.data),
  renumberTeams: (categoryId: number) =>
    api.post<void>(`/admin/categories/${categoryId}/teams/renumber`).then((r) => r.data),
  deleteTeam: (id: number) => api.delete(`/admin/teams/${id}`),

  // Templates
  listTemplates: () =>
    api.get<CompetitionTemplate[]>('/admin/templates').then((r) => r.data),

  // Users (UNIT_ADMIN only — vidi sve unit)
  listUsers: () => api.get<User[]>('/admin/users').then((r) => r.data),
};
